import { useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { FieldConfig } from "../../formConfig";
import { searchPeople, getPersonProfile, photoUrl, PersonResult, PersonProfile } from "../../api/people";

export interface PersonValue {
  displayName: string;
  jobTitle?: string;
  department?: string;
  id?: string;
  isFreeform?: boolean;
}

function isPerson(v: unknown): v is PersonValue {
  return !!v && typeof v === "object" && "displayName" in (v as object);
}

/** Normalize any stored value (legacy string / single object / array) to an array of PersonValue. */
export function normalizePersons(v: unknown): PersonValue[] {
  if (v == null || v === "") return [];
  if (Array.isArray(v)) {
    return v
      .map((item) => (isPerson(item) ? item : typeof item === "string" && item.trim() ? { displayName: item, isFreeform: true } : null))
      .filter((x): x is PersonValue => !!x);
  }
  if (isPerson(v)) return [v];
  if (typeof v === "string" && v.trim()) return [{ displayName: v, isFreeform: true }];
  return [];
}

function Avatar({ id, name, size = 28 }: { id?: string; name: string; size?: number }) {
  const [errored, setErrored] = useState(false);
  const initials = useMemo(() => {
    const parts = name.split(/\s+/).filter(Boolean);
    return (parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "");
  }, [name]);
  const style: React.CSSProperties = { width: size, height: size, fontSize: Math.round(size * 0.42) };
  if (id && !errored) {
    return (
      <img
        className="avatar"
        style={style}
        src={photoUrl(id)}
        alt=""
        onError={() => setErrored(true)}
      />
    );
  }
  return <span className="avatar avatar-initials" style={style}>{initials.toUpperCase() || "?"}</span>;
}

function ProfileCard({ id, fallbackName, onClose }: { id: string; fallbackName: string; onClose: () => void }) {
  const [profile, setProfile] = useState<PersonProfile | null>(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    getPersonProfile(id).then((p) => {
      setProfile(p);
      setLoading(false);
    });
  }, [id]);
  return (
    <div className="profile-overlay" onClick={onClose}>
      <div className="profile-card" onClick={(e) => e.stopPropagation()}>
        <button type="button" className="profile-close" onClick={onClose} aria-label="Close">×</button>
        <div className="profile-header">
          <Avatar id={id} name={profile?.displayName ?? fallbackName} size={72} />
          <div>
            <div className="profile-name">{profile?.displayName ?? fallbackName}</div>
            {profile?.jobTitle && <div className="profile-title">{profile.jobTitle}</div>}
            {profile?.department && <div className="profile-dept">{profile.department}</div>}
          </div>
        </div>
        {loading && <div className="hint">Loading…</div>}
        {profile && (
          <dl className="profile-details">
            {profile.mail && (<><dt>Email</dt><dd><a href={`mailto:${profile.mail}`}>{profile.mail}</a></dd></>)}
            {profile.mobilePhone && (<><dt>Mobile</dt><dd><a href={`tel:${profile.mobilePhone}`}>{profile.mobilePhone}</a></dd></>)}
            {profile.businessPhones && profile.businessPhones.length > 0 && (<><dt>Office</dt><dd>{profile.businessPhones.join(", ")}</dd></>)}
            {profile.officeLocation && (<><dt>Location</dt><dd>{profile.officeLocation}</dd></>)}
          </dl>
        )}
      </div>
    </div>
  );
}

export default function PersonField({ field }: { field: FieldConfig }) {
  const { setValue } = useFormContext();
  const current = useWatch({ name: field.id });
  const people = useMemo(() => normalizePersons(current), [JSON.stringify(current)]);

  const max = field.maxPersons && field.maxPersons > 0 ? field.maxPersons : undefined;
  const atMax = max !== undefined && people.length >= max;

  const [text, setText] = useState("");
  const [suggestions, setSuggestions] = useState<PersonResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cardPerson, setCardPerson] = useState<PersonValue | null>(null);
  const blurTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!text || text.length < 2) {
      setSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await searchPeople(text);
        const targetTitle = field.question.trim().toLowerCase();
        const sorted = [...results].sort((a, b) => {
          const aMatch = (a.jobTitle ?? "").trim().toLowerCase() === targetTitle ? 0 : 1;
          const bMatch = (b.jobTitle ?? "").trim().toLowerCase() === targetTitle ? 0 : 1;
          if (aMatch !== bMatch) return aMatch - bMatch;
          return a.displayName.localeCompare(b.displayName);
        });
        setSuggestions(sorted);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [text, field.question]);

  const commit = (next: PersonValue[]) => {
    setValue(field.id, next, { shouldDirty: true, shouldTouch: true });
  };

  const alreadyAdded = (p: PersonValue) =>
    people.some((x) => (p.id && x.id ? x.id === p.id : x.displayName.toLowerCase() === p.displayName.toLowerCase()));

  const addPerson = (p: PersonValue) => {
    if (atMax || alreadyAdded(p)) return;
    commit([...people, p]);
    setText("");
    setSuggestions([]);
    setOpen(false);
  };

  const addFreeform = (value: string) => {
    const v = value.trim();
    if (!v) return;
    if (atMax) return;
    if (people.some((x) => x.displayName.toLowerCase() === v.toLowerCase())) {
      setText("");
      return;
    }
    commit([...people, { displayName: v, isFreeform: true }]);
    setText("");
  };

  const removeAt = (idx: number) => {
    commit(people.filter((_, i) => i !== idx));
  };

  return (
    <div className="person-field">
      {people.length > 0 && (
        <div className="person-chips">
          {people.map((p, idx) => (
            <span key={(p.id ?? p.displayName) + idx} className="person-chip">
              <Avatar id={p.id} name={p.displayName} size={20} />
              <span className="person-chip-name">{p.displayName}</span>
              {p.id && !p.isFreeform && (
                <button
                  type="button"
                  className="person-chip-view"
                  onClick={() => setCardPerson(p)}
                  title="View profile"
                >View</button>
              )}
              <button
                type="button"
                className="person-chip-remove"
                onClick={() => removeAt(idx)}
                aria-label={`Remove ${p.displayName}`}
              >×</button>
            </span>
          ))}
        </div>
      )}
      {!atMax && (
        <div className="person-input-wrap">
          <input
            type="text"
            autoComplete="nope"
            data-form-type="other"
            data-lpignore="true"
            data-1p-ignore
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addFreeform(text);
              }
            }}
            onBlur={() => {
              blurTimer.current = window.setTimeout(() => {
                setOpen(false);
                if (text.trim()) addFreeform(text);
              }, 150);
            }}
            placeholder="Start typing a name…"
          />
        </div>
      )}
      {atMax && <div className="hint">Maximum of {max} selected.</div>}
      {open && !atMax && (suggestions.length > 0 || loading) && (
        <div
          className="person-suggestions"
          onMouseDown={(e) => {
            e.preventDefault();
            if (blurTimer.current) window.clearTimeout(blurTimer.current);
          }}
        >
          {loading && <div className="person-loading">Searching…</div>}
          {suggestions.map((s) => (
            <button
              type="button"
              key={s.id}
              className="person-suggestion"
              disabled={alreadyAdded({ displayName: s.displayName, id: s.id })}
              onClick={() =>
                addPerson({
                  displayName: s.displayName,
                  jobTitle: s.jobTitle,
                  department: s.department,
                  id: s.id,
                  isFreeform: false
                })
              }
            >
              <Avatar id={s.id} name={s.displayName} size={32} />
              <span className="person-suggestion-text">
                <span className="person-name">{s.displayName}</span>
                {(s.jobTitle || s.department) && (
                  <span className="person-meta">{[s.jobTitle, s.department].filter(Boolean).join(" · ")}</span>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
      {cardPerson?.id && (
        <ProfileCard id={cardPerson.id} fallbackName={cardPerson.displayName} onClose={() => setCardPerson(null)} />
      )}
    </div>
  );
}
